// PaywallSheet (also the PlanPicker): opened by any element with data-entry-point. Headline from the trigger,
// the user's own numbers per plan (only what we scanned), Basic · Complete (preselected) · Ultimate, mock prices
// per month billed yearly, the bundle line, one primary CTA. Logs paywall_viewed on open, upgrade_started on the CTA.
// Upgrading the upgrade-moment persona runs its transition (new types, deep scan, map fills in).
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { paywallHeadline, PLAN_LABEL } from '../../domain/copy';
import { atLeast, PLANS } from '../../domain/entitlements';
import { planReach } from '../../domain/insights';
import type { Plan } from '../../domain/types';
import { useData } from '../app/DataProvider';
import { usePlan } from '../app/PlanProvider';
import { Button } from '../components/Button';
import { Sheet } from '../components/Sheet';
import { Icon } from '../icons';
import { track } from '../lib/track';
import { UpgradeTransition } from './UpgradeTransition';
import s from './PaywallSheet.module.css';

interface PaywallCtx { open: (entryPoint: string, need?: Plan) => void }
const Ctx = createContext<PaywallCtx>({ open: () => {} });
export const usePaywall = () => useContext(Ctx);

const OPTIONS: Plan[] = ['basic', 'complete', 'ultimate'];

export function PaywallProvider({ children }: { children: ReactNode }) {
  const { plan, setPlan } = usePlan();
  const { data } = useData();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [entry, setEntry] = useState<string | null>(null);
  const [choice, setChoice] = useState<Plan>('complete');
  const [transition, setTransition] = useState<{ before: string[]; plan: Plan } | null>(null);

  const open = useCallback((entryPoint: string, need: Plan = 'complete') => {
    setEntry(entryPoint);
    setChoice(atLeast(need, 'complete') ? need : 'complete');
    track('paywall_viewed', { entry_point: entryPoint });
  }, []);
  const close = useCallback(() => setEntry(null), []);

  const reach = useMemo(() => {
    if (!data) return null;
    const typeOf = (id: string) => data.watchlist.find((w) => w.id === id)?.type;
    return Object.fromEntries(OPTIONS.map((p) => [p, planReach(p, data.findings, typeOf)])) as Record<Plan, ReturnType<typeof planReach>>;
  }, [data]);

  const upgrade = async () => {
    if (!entry) return;
    const from = entry;
    track('upgrade_started', { entry_point: from, plan: choice });
    setEntry(null);
    const before = data?.findings.map((f) => f.id) ?? [];
    const r = await setPlan(choice);
    if (r?.events?.some((e) => e.name === 'scan_started' && e.props.trigger === 'upgrade')) setTransition({ before, plan: choice });
    else if (from === 'free_scan_result' || pathname === '/scan') navigate('/');
  };

  const total = reach?.complete.total ?? 0;
  const planReachNow = data && plan !== 'free_scan'
    ? planReach(plan, data.findings, (id) => data.watchlist.find((w) => w.id === id)?.type).visible : 0;
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <Sheet open={entry !== null} onClose={close} title={entry ? paywallHeadline(entry) : ''} testId="paywall-sheet">
        <div className={s.body} data-entry={entry ?? undefined} data-testid="plan-picker">
          {reach && total > 0 && (
            <p className={`bodySm ${s.muted}`} data-testid="paywall-numbers">
              We found {total} {total === 1 ? 'finding' : 'findings'} for you. You see {plan === 'free_scan' ? 0 : planReachNow} in full today.
            </p>
          )}
          <div className={s.plans} role="radiogroup" aria-label="Plan">
            {OPTIONS.map((p) => {
              const current = p === plan;
              const disabled = !atLeast(p, plan) || current;
              const r = reach?.[p];
              return (
                <button key={p} type="button" role="radio" aria-checked={choice === p} disabled={disabled}
                  className={s.plan} data-testid={`paywall-plan-${p}`} data-visible={r?.visible} onClick={() => setChoice(p)}>
                  <span className={s.planHead}>
                    <span className="bodyMdBold">{PLAN_LABEL[p]}</span>
                    {choice === p && <Icon name="circle-check" size={20} className={s.check} />}
                  </span>
                  <span className={`bodySm ${s.muted}`}>{PLANS[p].tagline}</span>
                  {r && r.total > 0 && (
                    <span className={`bodySmMedium ${s.count}`} data-testid={`plan-count-${p}`}>
                      Shows {r.visible === r.total ? `all ${r.total}` : `${r.visible} of ${r.total}`} findings in full{r.removable ? ` · removes ${r.removable} broker ${r.removable === 1 ? 'listing' : 'listings'}` : ''}
                    </span>
                  )}
                  <span className={s.price}><span className="bodyLgBold">€{PLANS[p].price?.toFixed(2)}</span> <span className={`bodyXs ${s.muted}`}>/ month, billed yearly</span></span>
                  {current && <span className={`bodyXsMedium ${s.muted}`}>Your plan</span>}
                </button>
              );
            })}
          </div>
          <p className={`bodyXs ${s.muted}`}>Included in your Nord bundle: VPN, Threat Protection and Password Manager.</p>
          <Button block onClick={upgrade} data-testid="paywall-cta">Get {PLAN_LABEL[choice]}</Button>
        </div>
      </Sheet>
      <UpgradeTransition open={transition !== null} before={transition?.before ?? []} plan={transition?.plan ?? 'complete'} onClose={() => setTransition(null)} />
    </Ctx.Provider>
  );
}

// Wraps any upgrade trigger so it carries data-entry-point and opens the sheet.
// Its visible content is the accessible name; `label` is a tooltip with the longer reason.
export function EntryPoint({ id, need, children, className, label }: { id: string; need?: Plan; children: ReactNode; className?: string; label: string }) {
  const { open } = usePaywall();
  return (
    <button type="button" className={className} data-entry-point={id} title={label} onClick={() => open(id, need)}>
      {children}
    </button>
  );
}
